import React, { useEffect, useState } from "react";
import { Button } from "@heroui/react";
import { toast } from "sonner";
import { createAbuseReport } from "../../services/api";
import { useTranslation } from "react-i18next";
import { CreateAbuseReportRequest, ReportAbusePopupProps } from "../../types/types";

export const ReportAbusePopup: React.FC<ReportAbusePopupProps> = ({
  isOpen,
  onClose,
  onSubmit, 
  cardId,
  type,
  reportedTo,
}) => {
  const [reportDescription, setReportDescription] = useState("");
  const [reportError, setReportError] = useState("");
  const { t, i18n } = useTranslation('reportAbusePopup');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!reportDescription.trim()) {
      setReportError(t("errorReasonRequired"));
      return;
    }

    if (reportDescription.length > 500) {
      setReportError(t("errorReasonTooLong"));
      return;
    }

    const payload: CreateAbuseReportRequest = {
      reportedTo,
      reason: reportDescription,
      type: type,
      cardId: cardId || undefined,
    };

    try {
      await createAbuseReport(payload);
      setReportError("");
      onSubmit(type, reportDescription);
      toast.success(t("submit_success"));
      setReportDescription(""); 
      onClose();
    } catch (error) {
      console.error("Failed to submit abuse report:", error);
      setReportError(t("errorReasonRequired"));
      toast.error(t("errorReasonRequired"));
    }
  };

  return (
    <div className="absolute bottom-16 right-6 bg-white p-4 rounded-xl shadow-xl w-80 z-50 border border-gray-200">
      <h3 className="text-sm font-semibold mb-2 text-gray-800">{t("reportAbuse")}</h3>

      <div className="mb-3">
        <label className="text-xs text-gray-600 block">{t("reasonLabel")}</label>
        <textarea
          value={reportDescription}
          onChange={(e) => {
            setReportDescription(e.target.value);
            setReportError("");
          }}
          rows={3}
          maxLength={500}
          className="w-full p-2 border rounded-md text-sm"
          placeholder={t("reasonPlaceholder")}
        />
        <div className="text-xs text-gray-500 text-right">
          {reportDescription.length}{t("characterCount")}
        </div>

      </div>

      {reportError && (
        <p className="text-xs text-red-500 mb-2">{reportError}</p>
      )}

      <div className="flex justify-end gap-2">
        <Button
          onPress={onClose}
          className="text-xs bg-gray-200 text-gray-700 px-3 py-1 rounded-md hover:bg-gray-300 transition"
        >
          {t("cancel")}
        </Button>
        <Button
          onPress={handleSubmit}
          className="text-xs bg-red-500 text-white px-3 py-1 rounded-md hover:bg-red-600 transition"
        >
          {t("submit")}
        </Button>
      </div>
    </div>
  );
};